// Moniteur de modèles IA — santé et statut de tous les modèles d'intelligence artificielle
import { Brain, RefreshCw, CheckCircle, Clock, XCircle, Activity } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import React from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ModelStatus = "active" | "training" | "error";

interface AIModel {
  readonly id: string;
  readonly name: string;
  readonly status: ModelStatus;
  readonly last_update: string;
  readonly accuracy_percent: number;
  readonly next_scheduled: string;
}

interface ActivityLog {
  readonly id: string;
  readonly action: string;
  readonly model: string;
  readonly date: string;
  readonly result: "success" | "pending" | "failed";
}

// ---------------------------------------------------------------------------
// Mock data (to be replaced by real API when available)
// ---------------------------------------------------------------------------

const MODELS: readonly AIModel[] = [
  {
    id: "forecast",
    name: "Prévision demande",
    status: "active",
    last_update: "2026-03-24",
    accuracy_percent: 87,
    next_scheduled: "2026-03-31",
  },
  {
    id: "pricing",
    name: "Tarification intelligente",
    status: "active",
    last_update: "2026-03-23",
    accuracy_percent: 82,
    next_scheduled: "2026-03-30",
  },
  {
    id: "churn",
    name: "Risque désabonnement",
    status: "training",
    last_update: "2026-03-22",
    accuracy_percent: 79,
    next_scheduled: "2026-03-29",
  },
  {
    id: "recommendations",
    name: "Recommandations",
    status: "active",
    last_update: "2026-03-24",
    accuracy_percent: 91,
    next_scheduled: "2026-03-31",
  },
  {
    id: "anomalies",
    name: "Détection anomalies",
    status: "active",
    last_update: "2026-03-21",
    accuracy_percent: 94,
    next_scheduled: "2026-03-28",
  },
  {
    id: "sentiment",
    name: "Analyse sentiment",
    status: "error",
    last_update: "2026-03-20",
    accuracy_percent: 0,
    next_scheduled: "—",
  },
];

const ACTIVITY_LOG: readonly ActivityLog[] = [
  { id: "1", action: "Entraînement automatique", model: "Recommandations", date: "2026-03-24", result: "success" },
  { id: "2", action: "Entraînement automatique", model: "Prévision demande", date: "2026-03-24", result: "success" },
  { id: "3", action: "Réentraînement forcé", model: "Risque désabonnement", date: "2026-03-22", result: "pending" },
  { id: "4", action: "Entraînement automatique", model: "Analyse sentiment", date: "2026-03-20", result: "failed" },
  { id: "5", action: "Entraînement automatique", model: "Détection anomalies", date: "2026-03-21", result: "success" },
];

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<
  ModelStatus,
  { label: string; className: string; Icon: React.ElementType }
> = {
  active: { label: "Actif", className: "bg-green-100 text-green-700", Icon: CheckCircle },
  training: { label: "Entraînement", className: "bg-amber-100 text-amber-700", Icon: Clock },
  error: { label: "Erreur", className: "bg-red-100 text-red-700", Icon: XCircle },
};

const RESULT_CONFIG: Record<
  "success" | "pending" | "failed",
  { label: string; className: string }
> = {
  success: { label: "Réussi", className: "text-green-700" },
  pending: { label: "En cours", className: "text-amber-600" },
  failed: { label: "Échoué", className: "text-red-600" },
};

function formatDate(d: string): string {
  if (d === "—") return "—";
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Model card component
// ---------------------------------------------------------------------------

interface ModelCardProps {
  readonly model: AIModel;
}

function ModelCard({ model }: ModelCardProps) {
  const status = STATUS_CONFIG[model.status];
  const StatusIcon = status.Icon;

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
      {/* Card header */}
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-stone-100">
            <Brain className="h-5 w-5 text-stone-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-stone-900">{model.name}</p>
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${status.className}`}
        >
          <StatusIcon className="h-3.5 w-3.5" />
          {status.label}
        </span>
      </div>

      {/* Metrics */}
      <div className="space-y-2 text-sm">
        {model.status !== "error" && (
          <div className="flex items-center justify-between">
            <span className="text-stone-500">Précision</span>
            <span className="font-semibold text-stone-900">{model.accuracy_percent}%</span>
          </div>
        )}
        {model.status === "error" && (
          <div className="rounded-md bg-red-50 p-2 text-xs text-red-600">
            Dernier entraînement échoué. Réentraînement requis.
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-stone-500">Dernière MàJ</span>
          <span className="text-stone-700">{formatDate(model.last_update)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-stone-500">Prochaine MàJ</span>
          <span className="text-stone-700">{formatDate(model.next_scheduled)}</span>
        </div>
      </div>

      {/* Action */}
      <div className="mt-4 border-t border-stone-100 pt-4">
        <button
          type="button"
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-stone-100 px-3 py-2 text-xs font-medium text-stone-700 hover:bg-stone-200"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Forcer réentraînement
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function ModelMonitor() {
  return (
    <AdminLayout>
      <div className="space-y-8 p-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Moniteur de modèles IA</h1>
          <p className="mt-1 text-sm text-stone-500">
            Santé, précision et planification de l'entraînement des modèles d'IA
          </p>
        </div>

        {/* Summary bar */}
        <div className="flex flex-wrap items-center gap-6 rounded-lg border border-stone-200 bg-white p-4">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-stone-400" />
            <span className="text-sm text-stone-500">Modèles actifs :</span>
            <span className="text-sm font-semibold text-green-700">
              {MODELS.filter((m) => m.status === "active").length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-stone-400" />
            <span className="text-sm text-stone-500">En entraînement :</span>
            <span className="text-sm font-semibold text-amber-600">
              {MODELS.filter((m) => m.status === "training").length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <XCircle className="h-4 w-4 text-stone-400" />
            <span className="text-sm text-stone-500">En erreur :</span>
            <span className="text-sm font-semibold text-red-600">
              {MODELS.filter((m) => m.status === "error").length}
            </span>
          </div>
        </div>

        {/* Model cards grid */}
        <section>
          <h2 className="mb-4 text-base font-semibold text-stone-800">Modèles déployés</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {MODELS.map((model) => (
              <ModelCard key={model.id} model={model} />
            ))}
          </div>
        </section>

        {/* Activity log */}
        <section>
          <h2 className="mb-4 text-base font-semibold text-stone-800">Journal d'activité</h2>
          <div className="overflow-hidden rounded-lg border border-stone-200">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">
                    Modèle
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">
                    Résultat
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 bg-white">
                {ACTIVITY_LOG.map((entry) => {
                  const result = RESULT_CONFIG[entry.result];
                  return (
                    <tr key={entry.id} className="hover:bg-stone-50">
                      <td className="px-4 py-3 text-sm text-stone-700">{entry.action}</td>
                      <td className="px-4 py-3 text-sm font-medium text-stone-900">{entry.model}</td>
                      <td className="px-4 py-3 text-sm text-stone-500">
                        {formatDate(entry.date)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-medium ${result.className}`}>
                          {result.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}
